import { Form, FormValues, Spin, UploadFile } from 'antd';
import { addMonths, formatISO } from 'date-fns';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import FileUpload from '../../../../shared/components/file-upload/file-upload';
import Modal from '../../../../shared/components/modal/modal';
import { CustomerFormSendFileModel } from '../../models/customer-form-send-file-model';
import { maxFileListSizeLimitValidatorRule } from '../../../../shared/utils/file-handling/file-handling';
import { useAppSelector } from '../../../../store/hooks';
import { useUploadArchiveFilesMutation } from '../../../archive/api/archive-api';
import LazyModal, { LazyModalComponentProps } from '../../../ui/components/lazy-modal';
import { closeModal, setAlert } from '../../../ui/store/ui-slice';
import { useGetCustomerQuery, useUploadCustomerFilesMutation } from '../../api/customers-api';
import { customersAlertId, customerSendFileModalId } from '../../constants/constants';
import { selectActiveCustomerId, setActiveCustomerId } from '../../store/customers-slice';
import styles from './customer-form-send-file.module.css';

const CustomerSendFileContent = ({ isOpen, afterClose }: LazyModalComponentProps): JSX.Element => {
  const { t } = useTranslation(['customers', 'common']);
  const dispatch = useDispatch();
  const activeCustomerId = useAppSelector(selectActiveCustomerId);
  const [fileList, setFileList] = useState<UploadFile | undefined>(undefined);
  const [form] = Form.useForm<CustomerFormSendFileModel>();
  const { data: customerData } = useGetCustomerQuery(activeCustomerId || '', {
    skip: !activeCustomerId,
  });
  const [uploadCustomerFiles, { isLoading: isUploadCustomerFilesLoading }] = useUploadCustomerFilesMutation();
  const [uploadArchiveFiles, { isLoading: isUploadArchiveFilesLoading }] = useUploadArchiveFilesMutation();

  const closeCustomerSendFileForm = () => {
    dispatch(closeModal(customerSendFileModalId));
    dispatch(setActiveCustomerId(null));
  };

  const errorHandler = () => {
    form.setFields([
      {
        name: 'files',
        errors: [t('errors.unexpected', { ns: 'common' })],
      },
    ]);
    throw new Error('Something went wrong');
  };

  const onSubmit = async (formData: FormValues<typeof form>) => {
    if (!customerData) {
      return;
    }
    const uploadedArchiveFilesIds = await Promise.all(
      (formData.files || []).map(file =>
        uploadArchiveFiles({ file })
          .unwrap()
          .then(({ id }) => id)
          .catch(error => {
            if (fileList) {
              fileList.status = 'error';
              fileList.response = error.data.errors.content?.[0] || t('customerSendFileModal.failedUpload');
            }
            return errorHandler();
          }),
      ),
    );

    uploadCustomerFiles({
      customerId: customerData.id,
      files: uploadedArchiveFilesIds,
      phone: customerData.phone,
      email: customerData.email,
      linkExpiresAt: formatISO(addMonths(new Date(), 1)),
    })
      .unwrap()
      .then(() => {
        closeCustomerSendFileForm();
        dispatch(
          setAlert({
            id: customersAlertId,
            type: 'success',
            title: t('customerSendFileModal.uploadSuccess'),
          }),
        );
      })
      .catch(() => errorHandler());
  };

  useEffect(() => form.setFieldValue('files', [fileList?.originFileObj]), [fileList, form]);

  return (
    <Modal
      title={
        <Spin spinning={!customerData}>
          <Trans i18nKey="customerSendFileModal.title" ns="customers" t={t} values={{ email: customerData?.email }} />
        </Spin>
      }
      onCancel={() => (!isUploadCustomerFilesLoading || !isUploadArchiveFilesLoading) && closeCustomerSendFileForm()}
      isLoading={isUploadCustomerFilesLoading || isUploadArchiveFilesLoading}
      customSubmitText={t('action.send', { ns: 'common' })}
      onOk={() => form.submit()}
      isOpen={isOpen}
      afterClose={afterClose}
    >
      <Form
        form={form}
        initialValues={{
          files: [],
        }}
        layout="vertical"
        onFinish={onSubmit}
      >
        <div className={styles.modalContainer}>
          <Form.Item
            name="files"
            rules={[
              { required: true, message: t('customerSendFileModal.filesRequired') },
              {
                validator: maxFileListSizeLimitValidatorRule(1000_0000),
                validateTrigger: ['onChange'],
                message: t('customerSendFileModal.uploadLimitReached'),
              },
            ]}
          >
            <FileUpload
              fileUploadProps={{
                maxCount: 1,
                onChange: ({ fileList }) => setFileList(fileList[0]),
                onRemove: () => setFileList(undefined),
                beforeUpload: () => false,
              }}
              fileUploadDescription={t('fileUpload.uploadDescriptionSingleUpload', { ns: 'common' })}
            />
          </Form.Item>
        </div>
      </Form>
    </Modal>
  );
};

const CustomerFormSendFile = () => {
  return <LazyModal modalId={customerSendFileModalId} Component={CustomerSendFileContent} />;
};

export default CustomerFormSendFile;
